import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Table,
  Row,
  Col,
  InputNumber,
  Button,
  Form,
  Input,
  Select,
  message,
  Switch,
  Affix,
  Upload,
} from "antd";
import { constructDisplayPrice, randomNumberGenerator } from "utils/general";
import {
  EyeOutlined,
  DeleteOutlined,
  DownOutlined,
  SaveOutlined,
  HomeOutlined,
  InboxOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { isArray } from "util";
import { patchAdviseeAttachment } from "apis/advisee";
import { useParams, Prompt } from "react-router-dom";
import { useFetchConstant } from "../../../../../../../apis/constant";
import Title from "components/Title/Title";
import UploadDragger from "components/Upload/Upload.Dragger";
import Locale from "react-intl-universal";

const EditableContext = React.createContext(null);

const EditableType = {
  number: "number",
  text: "text",
  selector: "selector",
  switch: "switch",
};
const FixPaymentCategory = {
  loanPayments: "loanPayments",
  fixedPayments: "fixedPayments",
  variablePayments: "variablePayments",
};

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  record,
  editable,
  children,
  dataIndex,
  handleSave,
  editableType,
  selectorChildren,
  expensesConstant,
  fixPaymentCategory,
  selectorDisableOthers,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const [editing, setEditing] = useState(false);
  const [incomeType, setIncomeType] = useState([]);

  useEffect(() => {
    if (editing && dataIndex !== "type") {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (selectorChildren && fixPaymentCategory && incomeType.length === 0) {
      setIncomeType(selectorChildren[fixPaymentCategory]);
    }
  }, [selectorChildren, fixPaymentCategory]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      editableType === EditableType.selector ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
          ]}
        >
          <Select
            defaultOpen
            autoFocus
            onBlur={save}
            showSearch
            onSearch={(e) => {
              if (selectorDisableOthers) {
                return;
              }

              var code = "";

              switch (fixPaymentCategory) {
                case FixPaymentCategory.variablePayments:
                  code = "otherVariablePayment";
                  break;

                case FixPaymentCategory.fixedPayments:
                  code = "otherFixedPayment";
                  break;

                default:
                  code = "otherIncome";
                  break;
              }

              var customIncomeType = {
                code: code,
                label: e,
              };
              var buffer = [...incomeType];
              buffer[buffer.length - 1] = customIncomeType;
              setIncomeType(buffer);
              handleSave({ ...record, label: e }, record.key);
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {incomeType.map((value) => {
              return (
                <Select.Option key={value.code} value={value.code}>
                  {value.code === "otherIncome" ||
                  value.code === "otherFixedPayment" ||
                  value.code === "otherVariablePayment"
                    ? `Others-${value.label}`
                    : value.label}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
      ) : editableType === EditableType.number ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
            {
              type: "number",
              message: "Invalid number format",
            },
          ]}
          initialValue={0}
        >
          <InputNumber
            min={0}
            precision={2}
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="100.00"
            style={{ width: "100%" }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/(,*)/g, "")}
          />
        </Form.Item>
      ) : editableType === EditableType.switch ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: false,
              message: `${title} is required.`,
            },
          ]}
          valuePropName="checked"
          initialValue={false}
        >
          <Switch
            ref={inputRef}
            onChange={save}
            onBlur={save}
            placeholder="Remarks"
            style={{ width: "100%" }}
            checkedChildren={"MRTA Covered"}
            unCheckedChildren={"N/A"}
          />
        </Form.Item>
      ) : (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: false,
              message: `${title} is required.`,
            },
          ]}
          initialValue={""}
        >
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="Remarks"
            style={{ width: "100%" }}
          />
        </Form.Item>
      )
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableTable = (props) => {
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    if (
      isArray(props.data) === true &&
      props.data.length > 0
      // &&
      // dataSource.length === 0
    ) {
      setDataSource(props.data);
    }
  }, [props.data]);

  const renderFileType = (filename) => {
    let formatIndex = String(filename).toLocaleLowerCase().lastIndexOf(".");
    let type = String(filename)
      .toLowerCase()
      .substring(formatIndex + 1, filename.length);

    switch (type) {
      case "jpeg":
      case "png":
      case "jpg":
      case "ico":
        return String("IMAGE/" + type.toUpperCase());

      case "pdf":
        return String("APPICATION/" + type.toUpperCase());
      default:
        return "-";
    }
  };

  var columnConfig = [
    {
      title: Locale.get("ownedPolicies.attachment.title"),
      dataIndex: "title",
      key: "title",
      width: "25%",
      fixed: "left",
      editable: true,
      editableType: EditableType.text,
      render: (value, record) => {
        return (
          <div
            style={{
              padding: 8,
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              borderRadius: 8,
              width: "auto",
            }}
          >
            <Row>
              <Col>
                <span>{value ? value : "-"}</span>
              </Col>
            </Row>
          </div>
        );
      },
    },
    {
      title: Locale.get("ownedPolicies.attachment.remarks"),
      dataIndex: "remarks",
      key: "remarks",
      width: "25%",
      editable: true,
      editableType: EditableType.text,
      render: (value, record) => {
        return (
          <div
            style={{
              padding: 8,
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              borderRadius: 8,
              width: "auto",
            }}
          >
            <span>{value ? value : "-"}</span>
          </div>
        );
      },
    },
    {
      title: Locale.get("ownedPolicies.attachment.fileType"),
      width: "10%",
      render: (record) => renderFileType(record.name),
    },
    ...props.additionalColumns,
    {
      title: Locale.get("ownedPolicies.attachment.action"),
      align: "end",
      width: "20%",
      fixPaymentCategory: props.category,
      render: (value, record, index) => {
        return (
          <Row justify="end">
            <Button
              type="link"
              //   style={{ color: P }}
              href={record.url}
              target="_blank"
              disabled={props.loading}
            >
              <EyeOutlined /> View
            </Button>

            <Button
              type="link"
              style={{ color: "red" }}
              disabled={props.loading}
              onClick={() => handleDelete(index)}
            >
              <DeleteOutlined /> Delete
            </Button>
          </Row>
        );
      },
    },
  ];

  const handleDelete = (dataIndex) => {
    var newData = dataSource.filter((item, index) => index !== dataIndex);
    setDataSource(newData);
    props.handleChildrenChanges(newData);
  };

  const handleAdd = (file) => {
    if (file && file.length === 0) {
      return;
    }

    var newData = {
      key: randomNumberGenerator(),
      title: "",
      remarks: "",
      _id: "",
      name: "",
      uid: "",
      url: "",
      ...file[0],
    };

    const output = [...dataSource, newData];
    setDataSource(output);
    props.handleChildrenChanges(output);
  };

  const handleSave = (row) => {
    const newData = [...dataSource];

    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setDataSource(newData);
    props.handleChildrenChanges(newData);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = columnConfig.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        title: col.title,
        handleSave: handleSave,
        editable: col.editable,
        dataIndex: col.dataIndex,
        editableType: col.editableType,
        expensesConstant: props.expensesConstant,
        fixPaymentCategory: col.fixPaymentCategory,
        selectorChildren: col.selectorChildren,
        selectorDisableOthers: col.selectorDisableOthers,
        width: col.width,
      }),
    };
  });

  return (
    <div style={{ marginTop: 32 }}>
      <Row style={{ marginBottom: 8, marginTop: 32 }}>
        <Col>{props.title}</Col>
      </Row>
      <Table
        rowKey="key"
        components={components}
        rowClassName={() => "editable-row"}
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        loading={props.loading}
        // scroll={{ x: window.screen.width < 1200 ? 1200 : false }}
        scroll={{ x: "auto" }}
      />
      <UploadDragger
        acceptTypes={["image/*", "application/pdf"]}
        isDisplayCard={false}
        onChange={(e) => {
          console.log(e, 502);
          handleAdd(e);
        }}
        onFileUploadChange={(e) => {
          console.log(e, 487);
        }}
        fileSizeLimitInMb={10}
        isMultiple={true}
        value={[]}
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

const MasterTable = (props) => {
  const [container, setContainer] = useState(null);
  const { id: adviseeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  const { selection: fixLoanType } = useFetchConstant("loanTypes");
  const { selection: fixPaymentType } = useFetchConstant("fixedPaymentTypes");
  const { selection: variablePaymentType } = useFetchConstant(
    "variablePaymentTypes"
  );
  const { selection: incomeTypes } = useFetchConstant("incomeTypes");
  const [saveReminder, setSaveReminder] = useState(false);

  useEffect(() => {
    if (isArray(props.data) === true && props.data.length > 0) {
      setEditIdentifierKey([...props.data]);
    }
  }, [props.data, props.isLoading]);

  useEffect(() => {
    const triggerReminder = (ev) => {
      if (saveReminder) {
        ev.preventDefault();
        return (ev.returnValue = "Are you sure you want to close?");
      }
    };

    if (saveReminder) {
      window.addEventListener("beforeunload", triggerReminder);
      window.addEventListener("unload", triggerReminder);
    } else {
      window.removeEventListener("beforeunload", triggerReminder);
      window.removeEventListener("unload", triggerReminder);
    }

    return () => {
      window.removeEventListener("beforeunload", triggerReminder);
      window.removeEventListener("unload", triggerReminder);
    };
  }, [saveReminder]);

  const setEditIdentifierKey = (data) => {
    if (!data) {
      return;
    }
    var buffer = [...data];
    buffer.forEach((x) => (x.key = randomNumberGenerator()));
    setDataSource(buffer);
  };

  const tables = [
    {
      name: "loanPayment",
      category: FixPaymentCategory.loanPayments,
      title: "",
      additionalColumns: [],
    },
  ];

  const handleCallSaveAPI = async () => {
    var payload = [...dataSource];

    for (let i = 0; i < payload.length; i++) {
      delete payload[i].key;
      delete payload[i]._id;
    }
    setLoading(true);
    patchAdviseeAttachment(adviseeId, payload)
      .then((res) => {
        console.log(res, 651);
        setLoading(false);
        message.success(Locale.get("ownedPolicies.attachment.successMsg"));
        setSaveReminder(false);
        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
      .catch((ex) => {
        setLoading(false);
        message.error(
          "Error in switching off the overridden setting. Please check your connection and try again."
        );
      });
  };

  const handleChildrenChanges = (data) => {
    setDataSource(data);
    setSaveReminder(true);
  };

  return (
    <div ref={setContainer}>
      <Affix offsetTop={0} target={() => container}>
        <Row
          justify="space-between"
          style={{ marginBottom: 16, marginTop: 16 }}
          align="middle"
        >
          <Col>
            <Title>{Locale.get("ownedPolicies.attachment.attachTitle")}</Title>
          </Col>
          <Col>
            <Button
              type="primary"
              loading={loading}
              icon={<SaveOutlined />}
              onClick={handleCallSaveAPI}
            >
              {Locale.get("ownedPolicies.attachment.saveButton")}
            </Button>
          </Col>
        </Row>
      </Affix>
      {tables.map((table) => (
        <EditableTable
          key={table.name}
          title={table.title}
          category={table.category}
          additionalColumns={table.additionalColumns}
          handleChildrenChanges={handleChildrenChanges}
          data={dataSource}
          template={[]}
          expensesConstant={[
            ...fixLoanType,
            ...variablePaymentType,
            ...fixPaymentType,
            ...incomeTypes,
          ]}
          incomeTypes={incomeTypes}
          fixLoanType={fixLoanType}
          fixPaymentType={fixPaymentType}
          variablePaymentType={variablePaymentType}
        />
      ))}
      <Prompt
        when={saveReminder}
        message={Locale.get("ownedPolicies.attachment.promptMessage")}
      />
    </div>
  );
};

export default MasterTable;

// export default EditableTable;
